import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/utils/axiosInterceptor';

// ==================== Types ====================
export interface OnlineEmployee {
  _id: string;
  empName: string;
  avatar: string;
  isOnline: boolean;
  lastSeen: Date;
  job: {
    jobName: string;
  };
}

export interface OnlineEmployeesResponse {
  success: boolean;
  data: OnlineEmployee[];
  count: number;
}

export interface EmployeeStatusResponse {
  success: boolean;
  data: {
    _id: string;
    empName: string;
    avatar: string;
    isOnline: boolean;
    lastSeen: Date;
  };
}

export interface PrivacySettings {
  showOnlineStatus?: boolean;
  showLastSeen?: boolean;
  whoCanSeeStatus?: 'everyone' | 'department' | 'managers' | 'nobody';
  allowedUsers?: string[]; // Array of employee IDs
}

export interface UpdatePrivacySettingsPayload {
  privacySettings: PrivacySettings;
}

// ==================== API Functions ====================

/**
 * Get all online employees
 */
export async function getOnlineEmployees(): Promise<OnlineEmployeesResponse> {
  const response = await api.get('/employee/online-employee');
  return response.data;
}

/**
 * Get specific employee status
 */
export async function getEmployeeStatus(
  employeeId: string,
): Promise<EmployeeStatusResponse> {
  const response = await api.get(`/employee/status/${employeeId}`);
  return response.data;
}

/**
 * Update privacy settings (backend endpoint needs to be implemented)
 */
export async function updatePrivacySettings(
  settings: PrivacySettings,
): Promise<{ success: boolean; message: string }> {
  const response = await api.put('/employee/privacy-settings', {
    privacySettings: settings,
  });
  return response.data;
}

// ==================== React Query Hooks ====================

/**
 * Hook to fetch all online employees
 * Automatically refetches every 30 seconds as backup to WebSocket
 */
export const useGetOnlineEmployees = () => {
  return useQuery({
    queryKey: ['employees', 'online'],
    queryFn: getOnlineEmployees,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 20000, // Consider stale after 20 seconds
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to fetch specific employee status
 */
export const useGetEmployeeStatus = (employeeId?: string) => {
  return useQuery({
    queryKey: ['employee', 'status', employeeId],
    queryFn: () => getEmployeeStatus(employeeId!),
    enabled: !!employeeId,
    staleTime: 10000, // 10 seconds
    retry: 2,
  });
};

/**
 * Hook to update privacy settings
 */
export const useUpdatePrivacySettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePrivacySettings,
    onSuccess: () => {
      // Invalidate employee queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['employee'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: (error: any) => {
      console.error('Failed to update privacy settings:', error);
    },
  });
};
